import { SystemClock, type Clock } from "@beacon/clock";
import { latLngToCell, cellToParent, cellToLatLng } from "h3-js";
import { RelayTransport } from "@beacon/transport/relay";
import { MatcherClient } from "../platform/matcher-transport.js";
import { BrowserStore } from "../platform/store.js";
import { Session } from "../platform/session.js";
import { RelaySimClock, systemClockSource, type ClockSource } from "../platform/sim-clock.js";
import { t as translate, type Catalog } from "../i18n/catalog.js";
import { onboardingFlow, homeScreen } from "../screens/onboarding/index.js";
import { nowTab } from "../screens/now/now-tab.js";
import { NowMapController } from "../screens/now/now-map-controller.js";
import type { MapHandle } from "../screens/now/map-view.js";
import { cellsWithinRadius } from "../map/home-radius.js";
import { shell } from "../screens/shell.js";
import { IncidentStore, cellDistanceKm, type IncidentView } from "../screens/now/incident-model.js";
import { SpreadStore, AlertEngine, type AlertEvent } from "../screens/now/alert-model.js";
import { GroupCoalescer } from "../screens/now/alert-grouping.js";
import { RawReportStore, rawReportsFC } from "../screens/now/raw-report-model.js";
import { RawReportLayer } from "./raw-report-layer.js";
import { relayHeadSeq } from "../platform/relay-info.js";
import { fetchRelayPulse, pulseState, type RelayPulse } from "../platform/relay-pulse.js";
import { WeatherStore } from "../screens/now/weather-model.js";
import { makeAlertController } from "../screens/now/alert-controller.js";
import { buildPushToast, criticalTakeover, takeoverDismissLabel } from "../screens/now/alert-ui.js";
import { ToastStack } from "../screens/now/toast-stack.js";
import { TakeoverGate } from "../screens/now/takeover-gate.js";
import { incidentDetail } from "../screens/now/incident-detail.js";
import { reportDetail } from "../screens/now/report-detail.js";
import { rawReportsIcon } from "../screens/now/hazard-icons.js";
import { publishReport, corroborationInput, type ReportInput } from "../screens/now/report-publish.js";
import { HelpStore } from "../screens/help/help-model.js";
import { helpTab } from "../screens/help/help-tab.js";
import { helperModeCard } from "../screens/help/helper-mode.js";
import { requestScreen, type ThreadMessage } from "../screens/help/request-screen.js";
import { MatchStore } from "../screens/help/match-model.js";
import {
  publishOffer, publishAccept, publishAvailability, publishTransition, publishCoordinationNote,
  publishHelpRequest,
} from "../screens/help/help-publish.js";
import { youTab, beaconTab } from "../screens/you/you-tab.js";
import { flagSheet } from "../screens/now/flag-sheet.js";
import { publishFlag } from "../screens/now/flag-publish.js";
import { appealSheet } from "../screens/you/appeal-sheet.js";
import { publishAppeal } from "../screens/you/appeal-publish.js";
import { talkTab } from "../screens/talk/talk-tab.js";
import { decodeMessage } from "../screens/talk/message-model.js";
import { MessageSubscriptions, type ZoneSubSpec } from "../screens/talk/message-subscriptions.js";
import { TalkFeed, type FeedMode } from "../screens/talk/talk-feed.js";
import { publishMessage } from "../screens/talk/message-publish.js";
import { areaCells, regionCells, kmToRegionK, REGION_STEPS } from "../screens/talk/talk-geo.js";
import { talkZones, snapLiveCell, type TalkZone } from "../screens/talk/talk-zones.js";
import { ZoneFeeds } from "../screens/talk/zone-feeds.js";
import { ZonePicker } from "../screens/talk/talk-zone-picker.js";
import { HttpKeyRegistry, LabelFold, buildGeo, IDENTITY_TYPES, type LabelAction } from "@beacon/client-core";
import { publishConsent, publishShred, type ConsentPurpose } from "../screens/you/privacy-publish.js";
import { pushToast, button, modal } from "../design/components.js";
import { registerServiceWorker, enableAutoUpdate, subscribeForPush, vapidKeyFromMeta, requestNotificationPermission, notifOutcome, pushDiagText, lastSubscribeError, isNoPushServiceError } from "../sw/register.js";
import { writeNotifConfig } from "../sw/notif-config.js";
import { publishAlertSubscription } from "../platform/alert-subscription.js";
import { CommonsFeed, type CommonsState } from "../commons/feed.js";
import { parseTokens } from "../commons/tokens.js";
import type { Eip1193 } from "../commons/erc20.js";
import { commonsCard, type CommonsCardCtx } from "../screens/you/commons.js";
import { chipInContent } from "../screens/commons/chip-in.js";
import { transparencyScreen } from "../screens/commons/transparency.js";
import { AreaSubscriptions } from "./area-subscriptions.js";
import { ZoneLoadGate } from "./zone-load-gate.js";
import { LISBOA_CENTER, inSupportedRegion } from "../map/tile-source.js";
import type { Locale } from "../i18n/catalog.js";
import { TONE_ROTATE_MS, DEFAULT_TONE, type ToneId } from "../i18n/tone/index.js";
import type { HomePlace } from "../platform/store.js";
import type { Bucket } from "../screens/now/display-bucket.js";
import { defaultBucketLevel } from "../screens/now/display-bucket.js";
import { placesStatus, watchedIncidents, buildMonitoredRings, zoneRelation, type MonitoredRing } from "../platform/monitored-places.js";
import { WatchedZoneNotifier } from "../screens/now/watched-zones.js";
import { LiveFollowController } from "../platform/live-follow.js";
import { WakeLockController, type ReleasableLock } from "../platform/wake-lock.js";
import { StandingClient, type Standing } from "../platform/standing-transport.js";
import { IncidentsClient, seedEventsFromReadModel } from "../platform/incidents-transport.js";
import { SpreadClient, seedSpreadEventsFromReadModel } from "../platform/spread-transport.js";
import { WeatherClient, seedWeatherEventsFromReadModel } from "../platform/weather-transport.js";
import { appContext, installTarget } from "../platform/app-context.js";
import { createInstallPrompt } from "../platform/install-prompt.js";
import { shouldShowInstallHint } from "../platform/install-hint-eligibility.js";
import { installModalContent } from "../screens/install/install-modal.js";
import { installHint } from "../screens/install/install-hint.js";
import { createAlertSound } from "../audio/alert-sound.js";
import { installAudioPrimer } from "../audio/audio-primer.js";
import type { EarconSeverity } from "../audio/earcon.js";
import { earconSeverityFor, shouldChime } from "../screens/now/alert-sound-routing.js";

/** Whether a background (timer- or stream-driven) re-render may proceed.
 *
 *  `started` MUST be checked: render() replaces the root element wholesale, but the
 *  hashchange listener that makes the tab bar work is only wired in start() — which runs
 *  on onboarding completion. A background render before start() (e.g. the E9 30s timer
 *  firing while onboarding is still up, with the hash defaulting to "now") would eject the
 *  user from onboarding onto an inert Now tab: clicking a tab changes the URL but nothing
 *  re-renders. Gating on `started` guarantees the first render is always the one start()
 *  runs. The remaining conditions suppress churn while the user is elsewhere: a different
 *  tab is active, or an overlay/in-body sheet is open (which a body rebuild would destroy). */
export function canBackgroundRender(s: {
  started: boolean;
  activeTab: string;
  targetTab: string;
  overlayOpen: boolean;
  sheetOpen: boolean;
}): boolean {
  return s.started && s.activeTab === s.targetTab && !s.overlayOpen && !s.sheetOpen;
}

/** Map the Talk zone list to subscription specs: every zone streams its area; the
 *  selected zone backfills area history and (in region scope) streams + backfills
 *  its greater-region. */
export function buildZoneSpecs(
  zones: TalkZone[],
  selectedId: string,
  scope: "area" | "region",
  regionKm: number,
): ZoneSubSpec[] {
  const k = kmToRegionK(regionKm);
  return zones.filter((z) => z.cell).map((z) => {
    const selected = z.id === selectedId;
    const regionActive = selected && scope === "region";
    return {
      id: z.id,
      areaCells: areaCells(z.cell, z.radiusKm),
      regionCells: regionActive ? regionCells(z.cell, k) : [],
      selected,
      regionActive,
    };
  });
}

/** Resolve the relay base URL from a <meta name="beacon-relay"> or same origin. */
function relayBaseUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="beacon-relay"]');
  return meta?.content || location.origin;
}

/** Resolve the key-registry base from <meta name="beacon-keys"> or same-origin /keys.
 *  Broadcast epoch keys are GET here (to open others' sealed messages) and our own
 *  is uploaded (signed) — the webapp nginx proxies /keys/ → key-registry:8081/keys/. */
export function keysBaseUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="beacon-keys"]');
  return meta?.content || `${location.origin}/keys`;
}

/** Resolve the matcher (A7) base URL from a <meta name="beacon-matcher"> or same origin.
 *  Helper availability is POSTed here, never to the public firehose. */
function matcherBaseUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="beacon-matcher"]');
  return meta?.content || location.origin;
}

/** Resolve the moderation base URL from a <meta name="beacon-moderation"> or same origin.
 *  Flags + appeals are POSTed here (private app.aid.mod.*), never to the public firehose. */
function moderationBaseUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="beacon-moderation"]');
  return meta?.content || location.origin;
}

/** Whether in-app alerting includes the speculative wind-prior cone (parity with the
 *  alert-dispatcher's BEACON_ALERT_WIND_CONES). Default off — wind cones stay display-only. */
function alertWindConesEnabled(): boolean {
  return document.querySelector<HTMLMetaElement>('meta[name="beacon-alert-wind-cones"]')?.content === "1";
}

/** Resolve the alert-dispatcher (A6) base URL from <meta name="beacon-alerts"> or the
 *  same-origin /alerts proxy (nginx → alert-dispatcher:8094). Signed
 *  app.aid.alert.subscription envelopes are POSTed to <base>/subscriptions (private). */
function dispatcherBaseUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="beacon-alerts"]');
  return meta?.content || `${location.origin}/alerts`;
}

/** Resolve the corroboration service base URL from <meta name="beacon-corroboration"> or
 *  same-origin /corroboration proxy. Used only for read-only standing fetches (no auth). */
function corroborationBaseUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="beacon-corroboration"]');
  return meta?.content || `${location.origin}/corroboration`;
}

/** Resolve the spread (A5) service base URL from <meta name="beacon-spread"> or the
 *  same-origin /spread proxy. Used only for read-only estimate fetches (no auth).
 *  The spread collection endpoint is the base path itself (SpreadClient appends only
 *  the query string), so this points at /spread, not a prefix. */
function spreadBaseUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="beacon-spread"]');
  return meta?.content || `${location.origin}/spread`;
}

/** Resolve the /weather read-model base URL (served by the same spread service, separate
 *  nginx location). Read-only; WeatherClient appends only the query string. */
function weatherBaseUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="beacon-weather"]');
  return meta?.content || `${location.origin}/weather`;
}

/** Contribution service base URL (same-origin /contribute proxy by default). Used by the
 *  commons chip-in to request a fresh deposit address (no auth). */
export function contributionBaseUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="beacon-contribution"]');
  return meta?.content || location.origin;
}
/** The fixed res-8 commonsCell the host tags cost/contribution events with; clients
 *  subscribe to it (CL-1 locked res-8). Empty ⇒ commons disabled in this deployment. */
function commonsCell(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="beacon-commons-cell"]')?.content || "";
}
/** Allowlist of trusted commons issuer DIDs (the host signer identityId on cost/
 *  contribution events). v1 trust filter; the durable upgrade resolves app.aid.trust.credential. */
export function commonsIssuers(): string[] {
  const raw = document.querySelector<HTMLMetaElement>('meta[name="beacon-commons-issuers"]')?.content || "";
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}
/** Raw JSON of the accepted-tokens config (symbol/contract/decimals/chainId). */
function commonsTokensMeta(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="beacon-commons-tokens"]')?.content || "";
}

/** Default home cell (Lisbon) until the user picks one (res 8). */
function defaultCell(): string {
  const [lng, lat] = LISBOA_CENTER;
  return latLngToCell(lat, lng, 8);
}

/** Dev teleport: `?home=lat,lng` relocates the test user's home on load (res 8).
 *  Returns the parsed coordinates, or null when absent/malformed. */
function teleportFromUrl(): { lat: number; lng: number } | null {
  const raw = new URLSearchParams(location.search).get("home");
  if (!raw) return null;
  const [lat, lng] = raw.split(",").map((n) => Number(n.trim()));
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** Resolve the current position to a res-8 H3 cell, or null if unavailable/denied
 *  (JIT permission — the OS prompt fires here, on first actual use). */
function geolocateCell(): Promise<string | null> {
  if (!("geolocation" in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(latLngToCell(pos.coords.latitude, pos.coords.longitude, 8)),
      () => resolve(null),
      { timeout: 8000 },
    );
  });
}

/** JIT precise coordinates ("lat,lng") released need-to-know on accept; null if denied. */
function geolocateCoords(): Promise<string | null> {
  if (!("geolocation" in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(`${pos.coords.latitude.toFixed(5)},${pos.coords.longitude.toFixed(5)}`),
      () => resolve(null),
      { timeout: 8000 },
    );
  });
}

type TabId = "now" | "help" | "you" | "talk" | "beacon" | "commons";
function activeTabFromHash(): TabId {
  const name = location.hash.replace(/^#\/?/, "");
  return name === "help" ? "help" : name === "you" ? "you" : name === "talk" ? "talk"
    : name === "beacon" ? "beacon" : name === "commons" ? "commons" : "now";
}

/** Download a JSON object as a file (jsdom-guarded: no-ops where URL.createObjectURL is absent). */
function downloadJson(filename: string, obj: unknown): void {
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return;
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Prompt the user to pick a recovery file and read it as text (jsdom-guarded → null). */
function pickFile(): Promise<string | null> {
  if (typeof document === "undefined" || typeof FileReader === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsText(file);
    });
    input.click();
  });
}

/** Browser entry: bootstrap the session, then render onboarding or the tabbed app. */
export async function bootstrap(rootEl: HTMLElement): Promise<void> {
  const transport = new RelayTransport({ baseUrl: relayBaseUrl() });
  const matcher = new MatcherClient({ baseUrl: matcherBaseUrl() });

  // Clock domain (project_beacon_clock_domains): in BEACON_MODE=simulation the relay
  // stamps records on a virtual clock anchored at the Unix epoch. A sim-clock sync
  // (which 404s in production) decides the domain UP FRONT, BEFORE Session.start, so
  // the BeaconClient stamps our OWN records' createdAt / TIDs on sim-time too —
  // otherwise a human message lands ~56y ahead of agent broadcasts and the Talk feed
  // jumps. The same clock drives record-age display. The matcher runs on wall-clock,
  // so its issuedAt / availability-until stay on Date.now().
  let clock: Clock = new SystemClock();
  let clockSource: ClockSource = systemClockSource;
  // wallclock-allow: TID clkid spread (10-bit, distinct from the sim authority's), not a time read.
  const sim = new RelaySimClock({ baseUrl: relayBaseUrl(), clkid: Math.floor(Math.random() * 1024) });
  try {
    await sim.sync();
    clock = sim; // envelope createdAt / TIDs on sim-time
    clockSource = sim; // record-age display on sim-time
    // Re-anchor periodically to correct drift and pick up rate changes / jumps.
    setInterval(() => { void sim.sync().catch(() => {}); }, 15_000); // wallclock-allow: UI cadence (apps/webapp excluded from gate-30)
  } catch {
    /* production (no /sim/clock) or relay unreachable → wall-clock for both */
  }

  const session = await Session.start({
    store: new BrowserStore(),
    transport,
    // Resolve broadcast epoch keys over HTTP (GET others' keys to open sealed
    // messages; PUT our own, signed via BeaconClient's uploader bind) — same-origin
    // /keys proxy. Without it the browser-local InMemoryKeyRegistry never fetches
    // others' keys, so every agent broadcast shows "🔒 message unavailable".
    registry: new HttpKeyRegistry({ baseUrl: keysBaseUrl() }),
    clock,
    accepted: [...navigator.languages],
  });
  const tt = (k: string, p?: Record<string, string | number>): string => translate(session.catalog as Catalog, k, p);

  // Offline shell + push receiver (graceful no-op without SW). On success, enable
  // silent auto-update so a new deploy reloads the open tab onto the new version.
  void registerServiceWorker().then((reg) => { if (reg) enableAutoUpdate(reg); });
  // Mirror the auto-dismiss preference into Cache Storage so the SW honours it even when the
  // app is closed (localStorage isn't visible to the SW). Absent ⇒ the render default applies.
  if (session.settings.notifyAutoDismissMs !== undefined) {
    void writeNotifConfig({ autoDismissMs: session.settings.notifyAutoDismissMs });
  }

  // Dev teleport (?home=lat,lng): relocate the test user's home before the first
  // render, so it bypasses onboarding and centers the map there. Persists via
  // setHome; on failure (e.g. offline relay) keep it in-memory so the UI follows.
  const teleport = teleportFromUrl();
  if (teleport) {
    const home: HomePlace = {
      cell: latLngToCell(teleport.lat, teleport.lng, 8),
      radiusKm: session.home?.radiusKm ?? 15,
      placeName: `${teleport.lat.toFixed(4)}, ${teleport.lng.toFixed(4)}`,
    };
    try {
      await session.setHome(home);
    } catch (err) {
      console.warn("[teleport] setHome failed; applied locally only:", err);
      session.home = home;
    }
    history.replaceState(null, "", location.pathname + location.hash); // drop the param so a reload doesn't re-force it
  }

  // Reporter standing: fetched once after session init; re-renders the You tab when it
  // arrives. Fail-soft — standing stays undefined on any error (static trust card shows).
  let standing: Standing | undefined;
  const standingClient = new StandingClient({ baseUrl: corroborationBaseUrl() });
  const incidentsClient = new IncidentsClient({ baseUrl: corroborationBaseUrl() });
  const spreadClient = new SpreadClient({ baseUrl: spreadBaseUrl() });
  const weatherClient = new WeatherClient({ baseUrl: weatherBaseUrl() });
  void standingClient.standing(session.did).then((s) => {
    standing = s;
    if (activeTabFromHash() === "you") render();
  }).catch((err) => { console.warn("[standing] unavailable:", err); });

  const onReport = async (input: ReportInput): Promise<void> => {
    await publishReport(session.client, input);
  };

  const incidents = new IncidentStore();
  // Liveness for the Now tab: the relay's pulse (polled) + when my own cells last got an event.
  let relayPulse: RelayPulse | null | undefined; // undefined = not polled yet (no line)
  let lastAreaEventMs: number | undefined;
  const noteAreaEvent = (): void => { lastAreaEventMs = Date.now(); }; // wallclock-allow: UI liveness (apps/webapp excluded from gate-30)
  const pollPulse = async (): Promise<void> => {
    const next = await fetchRelayPulse(relayBaseUrl());
    const changed = JSON.stringify(next) !== JSON.stringify(relayPulse);
    relayPulse = next;
    if (changed) bgRender("now");
  };
  const feedsBehindIncidents = (): string[] =>
    [...new Set(incidents.list().flatMap((v) => (v.contributors ?? []).map((c) => c.feed)).filter((f): f is string => !!f))].sort();
  const watchedNotifier = new WatchedZoneNotifier();
  const spread = new SpreadStore();
  const weather = new WeatherStore();

  // Draw every active incident's spread cone (observed + wind) on the main Now map. Each
  // observed cone carries its subject so a tap opens that incident (onSpreadClick). Called
  // on render and when a spread estimate arrives.
  const refreshSpreadCones = (h: {
    setSpreadCones: (cones: Array<{ subject: string; cells: string[]; apex?: [number, number]; bearingDeg?: number; speedKmh?: number; confidence?: number; horizonMinutes?: number }>) => void;
    setWindCone: (cones: Array<{ cells: string[]; apex?: [number, number]; windBearingDeg?: number; speedKmh?: number; confidence?: number; horizonMinutes?: number }>) => void;
    setConeCellsVisible: (on: boolean) => void;
  }): void => {
    const views = incidents.list();
    // Apex of the vector cone = the incident's representative cell centre. IncidentView.cell
    // can be "" (cellToLatLng would then return a bogus Arctic point); a missing apex makes
    // the map-view filter drop the vector cone, matching the old cells-only behaviour.
    const coneOf = (cell: string): [number, number] | undefined => {
      if (!cell) return undefined;
      const [lat, lng] = cellToLatLng(cell);
      return [lat, lng] as [number, number];
    };
    h.setSpreadCones(
      views.map((v) => {
        const s = spread.forSubject(v.subject);
        return {
          subject: v.subject,
          cells: s?.pathCells ?? [],
          apex: coneOf(v.cell),
          bearingDeg: s?.bearingDeg,
          speedKmh: s?.speedKmh,
          confidence: s?.confidence,
          horizonMinutes: s?.horizonMinutes,
        };
      }).filter((c) => c.cells.length > 0 || (c.apex && typeof c.bearingDeg === "number")),
    );
    h.setWindCone(
      views.map((v) => {
        const s = spread.forSubject(v.subject);
        return {
          cells: s?.windConeCells ?? [],
          apex: coneOf(v.cell),
          windBearingDeg: s?.windBearingDeg,
          windSpeedKmh: s?.windSpeedKmh,
          speedKmh: s?.speedKmh,
          confidence: s?.confidence,
          horizonMinutes: s?.horizonMinutes,
        };
      }),
    );
    h.setConeCellsVisible(!!session.settings.coneDebugCells?.enabled);
  };
  const rawStore = new RawReportStore(2000);
  const rawLayer = new RawReportLayer({
    client: session.client,
    store: rawStore,
    headSeq: () => relayHeadSeq(relayBaseUrl()),
    onChange: () => nowMap.acquire(home?.cell).handle.setRawReports(rawReportsFC(rawStore.list())),
  });
  rawLayer.setDepth(session.settings.rawReports?.depth ?? "medium");
  let rawShown = false;
  const help = new HelpStore(session.did);
  // My own help publishes are folded locally at publish time (pending) so they show at once;
  // persisted until the firehose echoes them (survives a reload), pruned after 24 h.
  help.restorePending(session.settings.helpPending ?? [], Date.now()); // wallclock-allow: persistence age
  const persistHelpPending = (): void => { void session.saveSettings({ helpPending: help.pendingSnapshot() }); };
  /** Fold a just-published help event (its signed JSON model) as pending and show it. */
  const foldOwnHelpEvent = (json: Record<string, unknown>): void => {
    help.ingest({ seq: 0, json, pending: true, atMs: Date.now() }); // wallclock-allow: persistence age
    persistHelpPending();
    bgRender("help");
  };
  const matchStore = new MatchStore();
  // Moderation: fold public trust.label events to surface actions against me (§7).
  const labels = new LabelFold();
  const modBase = moderationBaseUrl();
  // Home is reactive: setHomeState() rebinds these so a home adopted after bootstrap
  // (onboarding completion or the You-tab picker) takes effect with no page reload —
  // every closure below reads these `let`s at call time.
  // Toast host created early: the region check in setHomeState (below) appends to
  // it on the initial home too. (Stacks at top; overlays are full-screen.)
  const toastHost = document.createElement("div");
  toastHost.className = "toast-host";
  // Caps the alert-toast burst (e.g. changing home / adding a place replays every known
  // incident as a fresh alert) so it never buries the UI. Overflow folds into one tap-to-Now row.
  const alertToasts = new ToastStack(toastHost, {
    overflowText: (n) => tt("alert.toast.more", { n }),
    onOverflow: () => { location.hash = "#/now"; },
  });

  let home: HomePlace | undefined;
  let engine: AlertEngine | null;
  let coalescer = new GroupCoalescer();
  let parentCell: string;
  let lastWarnedCell = "";
  // Toast when home lands outside the served basemap extent (no tiles there).
  // Keyed on the cell so radius edits don't re-toast; re-entering the region re-arms it.
  const warnIfOutsideRegion = (h: HomePlace | undefined): void => {
    if (!h?.cell) { lastWarnedCell = ""; return; }
    const [lat, lng] = cellToLatLng(h.cell);
    if (inSupportedRegion(lat, lng)) { lastWarnedCell = ""; return; }
    if (h.cell === lastWarnedCell) return;
    lastWarnedCell = h.cell;
    const toast = pushToast({ title: tt("region.unsupported.title"), body: tt("region.unsupported.body") });
    toast.setAttribute("role", "status");
    toastHost.append(toast);
    // wallclock-allow: browser UI cadence (apps/webapp is gate-30 excluded)
    setTimeout(() => toast.remove(), 9000);
  };
  const setHomeState = (h: HomePlace | undefined): void => {
    home = h;
    engine = h?.cell ? new AlertEngine(h.cell, h.radiusKm, alertWindConesEnabled()) : null;
    coalescer = new GroupCoalescer(); // fresh active-sets when the home zone changes
    parentCell = h?.cell ? cellToParent(h.cell, 7) : "";
    warnIfOutsideRegion(h);
  };
  setHomeState(session.home);

  const overlayHost = document.createElement("div");
  overlayHost.className = "overlay-host";
  // Install pointer (M3): which runtime are we in, and the dismissible-hint host.
  // Computed once at boot — context/target don't change within a session.
  const appCtx = appContext();
  const appTarget = installTarget();
  const installPrompt = createInstallPrompt();
  const hintHost = document.createElement("div");
  hintHost.className = "hint-host";
  const markEngaged = (): void => {
    if (!session.settings.installEngaged) void session.saveSettings({ installEngaged: true });
  };
  // The incident-detail overlay mounts its own mini-map per open; track its handle so
  // we destroy() it (release the WebGL context) when the overlay closes or re-opens —
  // otherwise each open leaks a context.
  let activeDetailMap: MapHandle | null = null;
  const destroyDetailMap = (): void => { activeDetailMap?.destroy(); activeDetailMap = null; };

  // --- Back-button layer stack ---------------------------------------------
  // Every visible overlay/modal owns ONE browser-history entry — the overlayHost detail
  // surfaces (detail/thread/takeover/flag/appeal/I-can-help) AND the in-tab form modals
  // (report/ask/change-home/erase). Android's/the browser's hardware back button pops the
  // TOP layer (runs its teardown) instead of leaving the app or switching tabs. In-app
  // ✕/back call the dismiss() returned by pushBackLayer(), which drives history.back() so
  // the DOM and the history stack stay in lockstep.
  type BackLayer = { teardown: () => void };
  const backLayers: BackLayer[] = [];
  const pushBackLayer = (teardown: () => void): (() => void) => {
    const layer: BackLayer = { teardown };
    backLayers.push(layer);
    history.pushState({ beaconLayers: backLayers.length }, "");
    return () => {
      const idx = backLayers.indexOf(layer);
      if (idx === -1) return;                   // already torn down
      history.go(-(backLayers.length - idx));   // pop this layer (and anything above it)
    };
  };
  addEventListener("popstate", () => { backLayers.pop()?.teardown(); });

  // overlayHost is a SINGLE replaceable layer: detail → I-can-help swaps content but keeps
  // one history entry. overlayDismiss is the handle to pop it; null when nothing is shown.
  let overlayDismiss: (() => void) | null = null;
  // True while a critical-alert takeover is mounted — the Now-tab commons card is
  // suppressed then (the Now tab belongs to the crisis). The You-tab card is never suppressed.
  let alertActive = false;
  const takeoverGate = new TakeoverGate(); // bounds critical full-screen takeovers (one at a time, no replay re-hijack)
  const teardownOverlayDom = (): void => { destroyDetailMap(); overlayHost.replaceChildren(); };
  const mountOverlay = (node: HTMLElement): void => {
    destroyDetailMap();
    overlayHost.replaceChildren(node);
    if (!overlayDismiss) {
      overlayDismiss = pushBackLayer(() => { teardownOverlayDom(); overlayDismiss = null; render(); });
    }
  };
  const closeOverlay = (): void => {
    openRequestId = null;
    helpComposeDraft = "";                          // don't leak a half-typed note into the next request's thread
    alertActive = false;                            // a dismissed takeover un-suppresses the Now commons card
    takeoverGate.abort();                            // closing the overlay abandons any queued criticals
    if (overlayDismiss) overlayDismiss();         // → popstate → teardown + reset
    else teardownOverlayDom();
  };
  // Navigate from inside the overlay to a tab: drop the overlay's (top) layer and turn its
  // history entry INTO the tab entry (replaceState, no orphan), then tear down + render.
  const navigateFromOverlay = (hash: string): void => {
    if (overlayDismiss) {
      backLayers.pop();
      overlayDismiss = null;
      history.replaceState(null, "", hash);
    } else {
      location.hash = hash;
    }
    teardownOverlayDom();
    render();
  };

  // --- Commons (CL-5): the self-funding mirror, computed entirely client-side ---
  let commonsState: CommonsState = { costs: [], contribs: [] };
  // How many contributions on the firehose are credited to *me*. Snapshotting this
  // when the chip-in modal opens lets us detect MY deposit landing (the watcher
  // publishes it ~10s after it's seen on-chain) and surface a live thank-you —
  // no hard reload. Baseline is taken AFTER backfill (at modal-open), so a fresh
  // increase is a genuinely new payment, not replayed history.
  const ownContribs = (s: CommonsState): number =>
    s.contribs.filter((c) => c.beneficiary === session.did).length;
  let chipInBaseline: number | null = null;
  // An open chip-in modal registers here to get a live "your deposit landed" ping
  // (drives the in-modal spinner → thank-you); the toast covers the closed case.
  let chipInOnReceipt: (() => void) | null = null;
  const hasActiveLocalAlert = (): boolean => alertActive;
  /** Host accounting currency for display — taken from the latest cost event, else EUR. */
  const hostCurrencyOf = (): string => {
    const latest = [...commonsState.costs].sort((a, b) =>
      Date.parse(b.period.start) - Date.parse(a.period.start))[0];
    return latest?.fiatCurrency || "EUR";
  };
  const goTransparency = (): void => { location.hash = "#/commons"; };
  const openChipIn = (): void => {
    const tokens = parseTokens(commonsTokensMeta());
    chipInBaseline = ownContribs(commonsState); // watch for MY next contribution to land
    mountOverlay(modal({
      title: tt("commons.chipIn.title"),
      content: chipInContent({
        t: tt,
        tokens,
        rail: "evm-stablecoin", // the single EVM rail in scope (CL-3); other rails are future specs
        beneficiary: session.did,
        requestAddress: async (rail) => {
          const res = await fetch(`${contributionBaseUrl()}/contribute/address`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ beneficiary: session.did, rail }),
          });
          if (!res.ok) throw new Error("address request failed");
          return res.json() as Promise<{ address: string; expiresAt: string; windowSeconds: number }>;
        },
        provider: (window as unknown as { ethereum?: Eip1193 }).ethereum ?? null,
        onDone: closeOverlay,
        watchReceipt: (onReceived) => {
          chipInOnReceipt = onReceived;
          return () => { if (chipInOnReceipt === onReceived) chipInOnReceipt = null; };
        },
      }),
      onClose: closeOverlay,
      sheetId: "commons-chip-in",
    }));
  };
  const commonsCardCtx = (): CommonsCardCtx => ({
    t: tt,
    state: commonsState,
    myDid: session.did,
    createdAtMicros: session.createdAtMicros,
    nowMicros: Date.now() * 1000, // wallclock-allow: display projection only (apps/webapp excluded from gate-30)
    onChipIn: openChipIn,
    onTransparency: goTransparency,
    hostCurrency: hostCurrencyOf(),
  });

  // Get Beacon on your phone (M3): one platform-adaptive install modal —
  // QR (desktop) / Add-to-Home-Screen (iOS) / Install button (Android). Browser-only.
  const openInstall = (): void => {
    if (overlayHost.querySelector("[data-sheet=get-app]")) return; // already open
    mountOverlay(modal({
      title: tt("install.title"),
      content: installModalContent({
        t: tt,
        target: appTarget,
        url: `${location.origin}/#/get`,
        installAvailable: installPrompt.available(),
        onInstall: () => void installPrompt.prompt(),
      }),
      onClose: closeOverlay,
      sheetId: "get-app",
    }));
  };

  // Flag sheet (E2): a private app.aid.mod.flag POSTed to the moderation service.
  const openFlag = (subject: string): void => {
    if (overlayHost.querySelector("[data-sheet=flag]")) return; // already open
    const sheet = flagSheet({
      t: tt,
      subject,
      onSubmit: async (input) => {
        await publishFlag(session.client, { ...input, cell: youHomeCell() }, `${modBase}/flag`);
      },
      onClose: closeOverlay,
    });
    mountOverlay(sheet);
  };

  const openIncident = (subject: string): void => {
    const view = incidents.list().find((v) => v.subject === subject);
    if (!view) return;
    // Relate the incident to the watch zone it is most inside of (home + saved + live),
    // not just home — so a fire near a saved zone still reads as near (D2 multi-zone).
    const rel = zoneRelation(monitoredRings(), view.cell);
    mountOverlay(
      incidentDetail({
        t: tt,
        view,
        spread: spread.forSubject(subject),
        weather: weather.forCell(view.cell), // grid-cell wind reading covering this incident
        // Underlying raw reports in this incident's cells (power-user) → reach the observations.
        reports: rawStore.list().filter((r) => view.cells.includes(r.cell)),
        onOpenReport: (id) => {
          const p = rawStore.list().find((r) => r.id === id);
          if (p) mountOverlay(reportDetail({ t: tt, report: p, onBack: closeOverlay }));
        },
        distanceKm: rel?.distanceKm,
        radiusKm: rel?.radiusKm,
        placeName: rel?.placeName ?? home?.placeName,
        nowMs: clockSource.nowMs(), // sim-aware: ages compare against sim-time createdAt
        onBack: closeOverlay,
        // "I can help" → a modal (over this detail) to register as a helper near the
        // incident; its ✕ returns here, "browse" opens the Help tab.
        onHelp: () => openIncidentHelp(view),
        // "I see it too" → corroborate by publishing my own report for the same hazard AT
        // THE INCIDENT'S CELL (never my home/live cell: no position leak, same cluster so no
        // second incident); the projector folds it into this incident's W (D2). No handler
        // (no button) when the incident carries no cell.
        ...(corroborationInput(view)
          ? { onSeeItToo: async () => { await publishReport(session.client, corroborationInput(view)!); } }
          : {}),
        onFlag: openFlag,
        onMapHandle: (h) => { activeDetailMap = h; },
      }),
    );
  };

  // "I can help" modal — reuses the helper-mode availability card so a neighbor can
  // register to help in this incident's area without leaving the alert, plus a shortcut
  // into the Help tab. Mounted over the detail; ✕ re-opens the detail it came from.
  // "Add here as a zone" (Now tab, live position outside every watched ring): the add-place
  // picker centred on the live cell, in a modal; confirm saves it like the You tab does.
  const openAddZoneAt = (cell: string): void => {
    const picker = homeScreen({
      t: tt,
      defaultCell: cell,
      compact: true,
      onConfirm: (h) => {
        closeOverlay();
        void session.addPlace({ cell: h.cell, radiusKm: h.radiusKm, placeName: h.placeName }).then(() => { subscribePlaces(); void publishSubscription(); render(); });
      },
      onBrowse: closeOverlay,
    });
    mountOverlay(modal({ title: tt("you.loc.addPlace"), content: picker, onClose: closeOverlay, sheetId: "add-zone" }));
  };

  const openIncidentHelp = (view: IncidentView): void => {
    const content = document.createElement("div");
    content.append(helperModeCard({
      t: tt,
      homeCell: youHomeCell(),
      parentCell,
      tierCaption: tt("help.helper.tierUnknown"),
      nowMs: Date.now(), // wallclock-allow: availability TTL is wall-clock (matcher domain)
      onRegister: async (input) => { await publishAvailability(session.client, matcher, input); startMatchStream(); },
    }));
    const browse = button({
      label: tt("incident.detail.helpBrowse"),
      variant: "secondary",
      onClick: () => navigateFromOverlay("#/help"),
    });
    browse.setAttribute("data-action", "browse-help");
    content.append(browse);
    mountOverlay(modal({
      title: tt("incident.detail.help"),
      content,
      onClose: () => openIncident(view.subject), // ✕ → back to the alert detail
      sheetId: "incident-help",
    }));
  };

  // Mounts the critical takeover for `ev`, labelling its dismiss button with how many more
  // criticals are queued behind it. Dismiss chains to the next queued one (mountOverlay
  // swaps content within the single overlay layer), or closes when the queue drains.
  const mountTakeover = (ev: AlertEvent): void => {
    alertActive = true; // suppress the Now-tab commons card while the crisis takeover is up
    mountOverlay(
      criticalTakeover({
        t: tt,
        event: ev,
        placeName: home?.placeName,
        queued: takeoverGate.queuedCount(),
        onDetails: openIncident,
        onDismiss: dismissTakeover,
      }),
    );
  };
  const dismissTakeover = (): void => {
    const nextEv = takeoverGate.next();
    if (nextEv) { mountTakeover(nextEv); return; } // show the next queued critical
    closeOverlay();
  };
  // A new critical queued while a takeover is already up: refresh the live dismiss-button
  // count without re-mounting (which would reset the open takeover).
  const refreshTakeoverLabel = (): void => {
    const btn = overlayHost.querySelector<HTMLElement>('[data-takeover] [data-action="dismiss"]');
    if (btn) btn.textContent = takeoverDismissLabel(tt, takeoverGate.queuedCount());
  };

  // Foreground alert sound (opt-in, default off). Inert until the user enables it
  // (which unlocks the AudioContext via the You-tab gesture). See spec 2026-06-28.
  const alertSound = createAlertSound();
  // Browsers block audio until the first user gesture. After a reload (no gesture yet) an
  // auto-firing alert — e.g. a critical takeover shown as incidents load — chimes into a
  // still-suspended context and is silent. So on the user's first interaction, arm the
  // AudioContext AND, if a critical takeover is already on screen (it fired silent moments
  // ago), sound it now that audio is live. Only when sound is enabled.
  if (session.settings.alertSoundEnabled) {
    installAudioPrimer(window, {
      unlock: () => {
        alertSound.unlock();
        // wallclock-allow: quiet-hours is a user-comfort window in local time.
        if (overlayHost.querySelector("[data-takeover]") && shouldChime(session.settings, new Date())) {
          alertSound.play("critical", session.settings.tone ?? DEFAULT_TONE);
        }
      },
    });
  }
  const chime = (ev: AlertEvent, tier?: EarconSeverity): void => {
    // wallclock-allow: quiet-hours is a user-comfort window in local time.
    if (shouldChime(session.settings, new Date())) {
      alertSound.play(tier ?? earconSeverityFor(ev), session.settings.tone ?? DEFAULT_TONE);
    }
  };

  const controller = makeAlertController(
    {
      toast: (ev) => {
        chime(ev);
        alertToasts.add(
          buildPushToast({ t: tt, event: ev, placeName: home?.placeName }),
          { onClick: () => openIncident(ev.subject) },
        );
      },
      takeover: (ev) => {
        // Bound the screen-hijack: one takeover at a time. Genuinely-new criticals QUEUE and
        // mount as each is dismissed; a replay of one already shown this session (e.g. zone
        // change) is suppressed to a capped, clickable toast so it can't refill the queue.
        const decision = takeoverGate.request(ev);
        // Chime once per decision: a genuinely-new critical (shown or queued) gets the full
        // critical earcon; a suppressed replay is visually downgraded to a toast, so it gets
        // the softer "warning" tone instead of re-blaring critical during the over-alert pilot.
        if (decision === "show") { chime(ev); mountTakeover(ev); return; }
        if (decision === "queued") { chime(ev); refreshTakeoverLabel(); return; }
        chime(ev, "warning");
        alertToasts.add(
          buildPushToast({ t: tt, event: ev, placeName: home?.placeName }),
          { onClick: () => openIncident(ev.subject) },
        );
      },
    },
    { level: (b) => session.settings.alertLevel?.[b as Bucket] ?? defaultBucketLevel(b) },
  );

  // --- Help overlays -------------------------------------------------------
  let openRequestId: string | null = null;
  let helpComposeDraft = "";
  // One adaptive overlay per request. Re-invoked by the firehose handler when a
  // matching event lands (reactive refresh), so offers/notes/state changes appear live.
  const openRequestScreen = async (requestId: string): Promise<void> => {
    openRequestId = requestId;
    if (!home?.cell) return;
    const homeCell = home.cell;
    const homePlaceName = home.placeName;
    const view = help.requestById(requestId);

    // Matched-but-unfetched (distant) request: ensure an area subscription over the
    // match's coarse cells so the record + subsequent events stream in, then show a
    // loading placeholder; the create-event ingest triggers a refresh that re-mounts.
    if (!view) {
      const mt = matchStore.get(requestId);
      if (mt) areas.ensure(`match:${requestId}`, mt.cells);
      const loading = document.createElement("section");
      loading.className = "screen request-screen";
      loading.setAttribute("data-request-screen", requestId);
      const p = document.createElement("p");
      p.className = "bx-body";
      p.textContent = tt("help.screen.loading");
      loading.append(p);
      mountOverlay(loading);
      return;
    }

    void session.markHelpRead(requestId, help.latestNoteSeq(requestId)).then(() => refreshHelpTabDot());

    // Confirm dialog before a destructive lifecycle transition (Done / Can't-make).
    const confirmThen = (kind: "done" | "cant", run: () => Promise<void>): void => {
      const back = () => void openRequestScreen(requestId); // ✕ / Cancel → return to the request
      const content = document.createElement("div");
      content.className = "help-confirm";
      const body = document.createElement("p");
      body.className = "bx-body";
      body.textContent = tt(`help.confirm.${kind}.body`);
      const actions = document.createElement("div");
      actions.className = "help-confirm__actions";
      const cancel = button({ label: tt("help.confirm.cancel"), variant: "secondary", onClick: back });
      cancel.setAttribute("data-action", "confirm-cancel");
      const confirm = button({ label: tt(`help.confirm.${kind}.confirm`), variant: "primary",
        onClick: () => { closeOverlay(); void run(); } });
      confirm.setAttribute("data-action", "confirm-ok");
      actions.append(cancel, confirm);
      content.append(body, actions);
      mountOverlay(modal({ title: tt(`help.confirm.${kind}.title`), content, onClose: back, sheetId: `help-confirm-${kind}` }));
    };

    let headlineText: string | undefined;
    if (view.headline) {
      try { headlineText = new TextDecoder().decode(await session.client.openBroadcast(view.headline)); }
      catch { headlineText = undefined; }
    }
    let preciseLocation: string | undefined;
    if (view.locationGrant) {
      try { preciseLocation = new TextDecoder().decode(await session.client.openNeedToKnow(view.locationGrant, requestId)); }
      catch { preciseLocation = undefined; }
    }
    const messages: ThreadMessage[] = [];
    for (const note of view.notes) {
      try {
        const text = new TextDecoder().decode(await session.client.openNeedToKnow(note.body, requestId));
        messages.push({ author: note.author, text, createdAtIso: note.createdAtIso });
      } catch { /* not a recipient (need-to-know) → skip */ }
    }
    const participants = [view.creator, ...view.acceptedHelpers];
    const offerNotes = new Map<string, string>();
    for (const offer of view.offers) {
      if (!offer.note) continue;
      try { offerNotes.set(offer.offerId, new TextDecoder().decode(await session.client.openBroadcast(offer.note))); }
      catch { /* note unopenable → omit */ }
    }

    mountOverlay(requestScreen({
      t: tt,
      view,
      me: session.did,
      myCell: homeCell,
      headlineText,
      distanceKm: cellDistanceKm(homeCell, view.cell),
      nowMs: clockSource.nowMs(),
      preciseLocation,
      messages,
      offerNotes,
      composeDraft: helpComposeDraft,
      onDraftChange: (text) => { helpComposeDraft = text; },
      onOffer: async (input) => { const res = await publishOffer(session.client, input); foldOwnHelpEvent(res.envelope.json); },
      onAccept: async (offerId, helperDid) => {
        const precise = (await geolocateCoords()) ?? homePlaceName ?? "shared location";
        try {
          // Seals the precise-location grant need-to-know to the helper — needs the
          // helper's encryption key in the directory (subscribeIdentities). If it hasn't
          // been folded yet (startup race), don't crash the tab: toast and let them retry.
          await publishAccept(session.client, { requestId, offerId, helperDid, preciseLocation: precise, cell: view.cell || homeCell });
          void openRequestScreen(requestId);
        } catch {
          toast(tt("help.accept.error"));
        }
      },
      onSend: async (text) => {
        await publishCoordinationNote(session.client, { requestId, recipients: participants, text, cell: view.cell || homeCell });
        helpComposeDraft = "";
        void openRequestScreen(requestId);
      },
      // Unlike onSend, onResend intentionally does NOT clear helpComposeDraft:
      // it re-shares an existing message rather than sending a composer draft.
      onResend: async (text) => {
        await publishCoordinationNote(session.client, { requestId, recipients: participants, text, cell: view.cell || homeCell });
        void openRequestScreen(requestId);
      },
      onDone: () => Promise.resolve(confirmThen("done", async () => {
        await publishTransition(session.client, { requestId, to: "done", cell: view.cell || homeCell });
      })),
      onCantMake: () => Promise.resolve(confirmThen("cant", async () => {
        await publishTransition(session.client, { requestId, to: "reopened", cell: view.cell || homeCell });
      })),
      onBack: closeOverlay,
    }));
  };

  // --- Render dispatch -----------------------------------------------------
  const mountTab = (tab: TabId, body: HTMLElement) => {
    const sh = shell({ t: tt, simulation: session.simulation, body, activeTab: tab, helpUnread: helpUnreadAny() });
    sh.append(toastHost, overlayHost); // fixed-position hosts; flow order irrelevant
    const nav = sh.querySelector(".tab-bar"); // hint sits in-flow just above the tab bar
    if (nav) sh.insertBefore(hintHost, nav); else sh.append(hintHost);
    rootEl.replaceChildren(sh);
  };

  const nowBody = () => {
    // Persistent map: reused across the per-event re-renders so it survives the
    // cold-start incident burst and keeps the home marker/radius drawn.
    const { mount, handle } = nowMap.acquire(home?.cell);
    refreshSpreadCones(handle);
    const allViews = incidents.list();
    const primaryStatus = placesStatus(allViews, primaryRingsArr(), 0);
    const primarySubjects = new Set(primaryStatus.inRadius.map((e) => e.view.subject));
    const watchedList = watchedIncidents(allViews, secondaryRingsArr(), primarySubjects)
      .map((e) => ({ view: e.view, distanceKm: e.distanceKm, zoneName: e.ring.placeName }));
    const tabEl = nowTab({
      t: tt,
      home,
      incidents: incidents.list(),
      simulation: session.simulation,
      nowMs: clockSource.nowMs(), // sim-aware: E9 fade thresholds compare against sim-time
      status: primaryStatus,
      watched: watchedList,
      incidentsLoaded: incidentsGate.loaded,
      loadState: incidentsGate.state,
      loadProgress: incidentsGate.progress_,
      // Manual retry once stalled: one fresh seed cycle (the periodic refresh keeps trying too).
      onRetry: () => { incidentsGate.retry(); areas.reseedIncidents(); },
      onReport,
      geolocate: geolocateCell,
      onOpenIncident: openIncident,
      onModalBack: pushBackLayer,
      // Raw-report list: only when the power-user layer is shown. Snapshot of the store,
      // refreshed on the normal render cadence (flood-safe — not re-rendered per report).
      rawReports: rawShown ? rawStore.list() : undefined,
      onOpenReport: (id) => {
        const p = rawStore.list().find((r) => r.id === id);
        if (p) mountOverlay(reportDetail({ t: tt, report: p, onBack: closeOverlay }));
      },
      mapMount: mount,
      mapHandle: handle,
      // Commons card in calm-times only — null while a crisis takeover is up.
      rings: monitoredRings(),
      network: relayPulse === undefined ? undefined : pulseState(relayPulse, Date.now()), // wallclock-allow: UI liveness (apps/webapp excluded from gate-30)
      area: { lastEventMs: lastAreaEventMs, feeds: feedsBehindIncidents() },
      live: liveExactRef
        ? { lat: liveExactRef.lat, lng: liveExactRef.lng, radiusKm: liveRingRef?.radiusKm ?? session.settings.liveFollow?.radiusKm ?? 5 }
        : null,
      cameraMode,
      onRecenter: () => { cameraMode = "follow"; nowMap.acquire(home?.cell).handle.setFollow(true); render(); },
      onAddCurrentZone: openAddZoneAt,
      onFitAll: () => {
        const staticRings = monitoredRings().filter((r) => r.kind !== "live").map((r) => ({ cell: r.cell, radiusKm: r.radiusKm }));
        const live = liveExactRef
          ? { lat: liveExactRef.lat, lng: liveExactRef.lng, radiusKm: liveRingRef?.radiusKm ?? session.settings.liveFollow?.radiusKm ?? 5 }
          : null;
        // Set fit BEFORE fitAll(): fitAll fires onFollowDisengaged, whose guard skips
        // when cameraMode is already "fit" (so it doesn't clobber us back to "free").
        cameraMode = "fit";
        nowMap.acquire(home?.cell).handle.fitAll(staticRings, live);
        render();
      },
    });
    // Raw-reports map toggle: gated on the master opt-in (settings.rawReports?.enabled).
    // Lives ON the map mount (top-left corner), distinct from the camera FABs (top-right).
    // Clear any prior toggle before re-adding (nowTab already clears camera-fab above).
    mount.querySelectorAll(".raw-fab").forEach((el) => el.remove());
    if (session.settings.rawReports?.enabled) {
      const rawBtn = document.createElement("button");
      rawBtn.type = "button";
      rawBtn.className = "raw-fab";
      rawBtn.setAttribute("data-action", "raw-reports-toggle");
      rawBtn.setAttribute("aria-label", tt("now.raw.toggle"));
      rawBtn.title = tt("now.raw.toggle");
      rawBtn.setAttribute("aria-pressed", rawShown ? "true" : "false");
      rawBtn.append(rawReportsIcon());
      if (rawShown) rawBtn.setAttribute("data-active", "");
      rawBtn.addEventListener("click", () => {
        rawShown = !rawShown;
        if (rawShown) void rawLayer.activate(currentViewCells());
        else rawLayer.deactivate();
        if (rawShown) rawBtn.setAttribute("data-active", "");
        else rawBtn.removeAttribute("data-active");
        rawBtn.setAttribute("aria-pressed", rawShown ? "true" : "false");
        bgRender("now"); // show/hide the raw-report list section
      });
      mount.append(rawBtn);
    }
    return tabEl;
  };

  const helpUnreadFor = (requestId: string): boolean => {
    const cursor = session.settings.helpReadCursors?.[requestId] ?? -1;
    return help.unreadFor(requestId, cursor, session.did);
  };
  // Any request I'm party to (mine or helping) with an unread note.
  const helpUnreadAny = (): boolean => {
    const { requests, helping } = help.myActivity();
    return [...requests, ...helping].some((r) => helpUnreadFor(r.requestId));
  };
  // Imperatively toggle the Help tab-bar dot on the LIVE DOM (works cross-tab, no re-render).
  const refreshHelpTabDot = (): void => {
    const tab = rootEl.querySelector<HTMLElement>('.tab[data-tab="help"]');
    if (!tab) return;
    const has = tab.querySelector(".tab__dot");
    if (helpUnreadAny() && !has) {
      const dot = document.createElement("span");
      dot.className = "tab__dot"; dot.setAttribute("aria-hidden", "true");
      tab.append(dot);
    } else if (!helpUnreadAny() && has) {
      has.remove();
    }
  };

  const helpBody = () => {
    // Cross-check matcher suggestions against the still-open requests (authoritative
    // display source: HelpStore), ranked best-first, into the help-tab's nearby section.
    const openSubjects = new Set(help.openRequests().map((r) => r.requestId));
    const nearby = matchStore.near(openSubjects).map((mt) => ({
      requestId: mt.subject, need: mt.need, urgency: mt.urgency, cell: mt.cells[0] ?? "",
    }));
    return helpTab({
      t: tt,
      homeCell: home?.cell ?? "",
      parentCell,
      tierCaption: tt("help.helper.tierUnknown"),
      nowMs: Date.now(),
      open: help.openRequests(),
      activity: help.myActivity(),
      me: session.did,
      nearby,
      unread: helpUnreadFor,
      loaded: helpGate.loaded,
      loadState: helpGate.state,
      onRetry: () => { helpGate.retry(); areas.reopenHelp(); },
      onRegisterAvailability: async (input) => {
        await publishAvailability(session.client, matcher, input);
        startMatchStream();
      },
      onOpenRequest: (id) => void openRequestScreen(id),
      onCreateRequest: async (input) => { const res = await publishHelpRequest(session.client, input); foldOwnHelpEvent(res.envelope.json); },
      onModalBack: pushBackLayer,
    });
  };

  // Neutral text toast (reuses the C4 toast host; auto-dismiss via the wallclock-allow pattern).
  const toast = (msg: string): void => {
    const el = pushToast({ title: msg, body: "" });
    toastHost.append(el);
    // wallclock-allow: browser render cadence, not a clock read (gate-30 excludes apps/webapp).
    setTimeout(() => el.remove(), 4000);
  };

  // On-device diagnostic toast (toast-logging) — long-lived and tap-to-dismiss so a
  // result can be read/transcribed on a phone with no remote debugging (the Android
  // Firefox installed-PWA case). Used to surface the push failure detail.
  const toastLog = (title: string, body: string): void => {
    const el = pushToast({ title, body });
    el.setAttribute("role", "status");
    el.style.cursor = "pointer";
    el.addEventListener("click", () => el.remove());
    toastHost.append(el);
    // wallclock-allow: browser UI cadence (apps/webapp is gate-30 excluded)
    setTimeout(() => el.remove(), 60000);
  };

  // Private per-helper match stream (A7). Opened once the helper registers
  // availability; reconnects with backoff; closed on identity erase. Matches
  // never touch the public firehose (no availability-harvest leak).
  let matchStreamHandle: { close(): void } | null = null;
  const startMatchStream = () => {
    if (matchStreamHandle) return;
    let closed = false;
    const ctrl = new AbortController();
    const loop = async () => {
      while (!closed) {
        try {
          const issuedAt = new Date(Date.now()).toISOString(); // wallclock-allow: UI; matcher enforces freshness
          // sign() requires geo: anchor the subscribe to the home cell (or the Lisbon default).
          const env = await session.client.sign({
            $type: "app.aid.match.subscribe", payload: { issuedAt }, geo: buildGeo(youHomeCell()),
          });
          await matcher.streamMatches(env.wire.bytes, {
            signal: ctrl.signal,
            onMatch: (m) => {
              const isNew = matchStore.upsert(m);
              if (isNew && activeTabFromHash() === "help") {
                toast(tt("help.nearby.toast", { need: tt(`need.${m.need}`).toLowerCase() }));
              }
              bgRender("help");
            },
          });
        } catch {
          // stream ended or errored — fall through to backoff + reconnect
        }
        if (closed) break;
        // wallclock-allow: reconnect backoff (browser cadence, gate-30 excludes apps/webapp)
        await new Promise((r) => setTimeout(r, 3000));
      }
    };
    void loop();
    matchStreamHandle = { close: () => { closed = true; ctrl.abort(); } };
  };

  const youHomeCell = (): string => home?.cell ?? defaultCell();
  const recoveryName = () => `beacon-recovery-${session.did.slice(-8)}.json`;

  // Appeal sheet (E2): a private app.aid.mod.appeal referencing the label's TID (§7).
  const openAppeal = (action: LabelAction): void => {
    if (overlayHost.querySelector("[data-sheet=appeal]")) return; // already open
    const sheet = appealSheet({
      t: tt,
      action: action.tid,
      onSubmit: async (input) => {
        await publishAppeal(session.client, { ...input, cell: youHomeCell() }, `${modBase}/appeal`);
      },
      onClose: closeOverlay,
    });
    mountOverlay(sheet);
  };
  // One settings ctx, two tabs: You (about the person) and Beacon (app behaviour).
  const settingsCtx = (): Parameters<typeof youTab>[0] => ({
      t: tt,
      home,
      did: session.did,
      liveFollowUnavailable,
      locale: session.locale,
      standing,
      settings: session.settings,
      consents: session.consents,
      actions: labels.actionsAgainst(session.did), // trust.label actions against me (E2 §7)
      onAppeal: openAppeal,
      onModalBack: pushBackLayer,
      defaultCell: youHomeCell(),
      onSetHome: (h) => void session.setHome(h).then(() => { markEngaged(); refreshHome(); }),
      onSetRadius: (km) => { if (home) void session.setHome({ ...home, radiusKm: km }).then(refreshHome); },
      onSetQuietHours: (q) => void session.saveSettings({ quietHours: q }).then(() => { void publishSubscription(); render(); }),
      onSetAlertLevel: (bucket: string, level: string) =>
        void session.saveSettings({ alertLevel: { ...session.settings.alertLevel, [bucket]: level } }).then(() => { void publishSubscription(); render(); }),
      onToggleConsent: (purpose, grant) => void (async () => {
        await publishConsent(session.client, {
          purpose: purpose as ConsentPurpose, action: grant ? "grant" : "withdraw", cell: youHomeCell(),
        });
        await session.recordConsent(purpose, grant ? "grant" : "withdraw");
        render();
      })(),
      onToggleNotifications: (on) => {
        if (!on) {
          void session.saveSettings({ notificationsEnabled: false }).then(() => { pushCreds = null; void publishSubscription(); render(); });
          return;
        }
        markEngaged();
        void enableNotifications();
      },
      onSetNotifyAutoDismiss: (ms) => {
        void session.saveSettings({ notifyAutoDismissMs: ms })
          .then(() => writeNotifConfig({ autoDismissMs: ms }))
          .then(render);
      },
      onSetLocale: (l: Locale) => void session.setLocale(l).then(render),
      // Persist only — the slider preview already updated live, and a full re-render
      // here would collapse the open Voice disclosure and jump scroll on touchend.
      onSetTone: (tone: ToneId) => void session.setTone(tone),
      onSetAlertSound: (on) => void session.saveSettings({ alertSoundEnabled: on }).then(render),
      onPreviewAlertSound: () => { alertSound.unlock(); alertSound.preview(session.settings.tone ?? DEFAULT_TONE); },
      onExportRecovery: () => downloadJson(recoveryName(), session.exportState()),
      onImportRecovery: () => void (async () => {
        const text = await pickFile();
        if (!text) return;
        try { await session.adoptRecovery(text); toast(tt("you.trust.importOk")); }
        catch { toast(tt("you.trust.importBad")); }
      })(),
      onRotateKeys: () => void (async () => {
        const { bundle } = await session.client.rotateKeys(youHomeCell());
        await session.adoptBundle(bundle);
        downloadJson(recoveryName(), session.exportState());
        toast(tt("you.trust.rotated"));
      })(),
      onExportData: () => downloadJson(`beacon-data-${session.did.slice(-8)}.json`, session.exportState()),
      onErase: () => void (async () => {
        await publishShred(session.client, { scope: "identity", target: session.did, cell: youHomeCell() });
        matchStreamHandle?.close();
        matchStreamHandle = null;
        wakeLock.stop();
        watchedNotifier.reset();
        takeoverGate.reset();
        await session.wipeLocal();
        toast(tt("you.erase.done"));
        location.hash = "#/";
        location.reload?.();
      })(),
      places: session.places,
      onSetLiveFollow: (v) => { if (v.enabled) liveFollowUnavailable = false; void session.setLiveFollow(v).then(() => { syncLiveFollow(); render(); }); },
      onSetKeepAwake: (on) => { void session.saveSettings({ keepAwake: on }).then(() => { syncLiveFollow(); render(); }); },
      onEditPlace: (p) => { void session.updatePlace(p.id, { cell: p.cell, radiusKm: p.radiusKm, placeName: p.placeName }).then(() => { subscribePlaces(); void publishSubscription(); render(); }); },
      onReorderPlace: (id, dir) => { void session.reorderPlace(id, dir).then(render); },
      onAddPlace: (p) => void session.addPlace(p).then(() => { subscribePlaces(); void publishSubscription(); render(); }),
      geolocate: geolocateCell, // "Use current location" → picker centred on the JIT fix
      onRemovePlace: (id) => void session.removePlace(id).then(() => { void publishSubscription(); render(); }),
      onGetApp: appCtx === "browser" ? openInstall : undefined,
      onSetRawReports: (next) => {
        void session.saveSettings({ rawReports: next });
        session.settings.rawReports = next;
        rawLayer.setDepth(next.depth);
        if (!next.enabled) { rawLayer.deactivate(); rawShown = false; }
        render();
      },
      onSetConeCells: (next) => {
        void session.saveSettings({ coneDebugCells: next });
        session.settings.coneDebugCells = next;
        nowMap.acquire(home?.cell).handle.setConeCellsVisible(next.enabled);
        render();
      },
    });
  const youBody = () => youTab(settingsCtx());
  const beaconBody = () => {
    const el = beaconTab(settingsCtx());
    // The commons card — app-side, always rendered on Beacon (alerts never suppress it; master §6).
    if (commonsCell()) el.append(commonsCard(commonsCardCtx()));
    return el;
  };

  // --- Talk tab (broadcast messaging) -------------------------------------
  const talkFeed = new TalkFeed({
    t: tt,
    // Manual retry once stalled: one fresh history walk for the selected zone (msgSubs is
    // constructed below; the button can only be tapped after boot).
    onRetry: () => { talkGate.retry(); msgSubs.retryBackfill(talkZoneId); },
  });
  let talkScope: "area" | "region" = "area";
  let talkRegionKm = REGION_STEPS[1].km; // default ~150 km
  let talkDraft = ""; // composer text preserved across scope/tab switches
  // "reset" rebuilds the feed at the bottom (follow); "preserve" (set on tab re-entry)
  // anchors at the last-seen message so the replayed gap reads as unread.
  let talkRenderMode: FeedMode = "reset";
  // Per-zone Talk state.
  let talkZoneId = "home";
  const feeds = new ZoneFeeds();
  // Held live-zone cell: snapped to res-6 footprint to avoid per-GPS-tick resubscription.
  let liveHeldCell = "";

  /** Returns the live zone {cell, radiusKm} if live-follow is active, else null.
   *  Uses snapLiveCell to hold the current cell stable until the user has moved
   *  a full res-6 neighbourhood (avoids per-GPS-tick subscription churn). */
  const liveZoneRef = (): { cell: string; radiusKm: number } | null => {
    if (!liveRingRef) return null;
    // Snap: liveRingRef.cell is always a valid res-8 cell when liveRingRef is non-null
    // (watchPosition sets both liveRingRef and liveExactRef together; clear does the same).
    const rawLiveCell = liveRingRef.cell; // already res-8 from watchPosition
    liveHeldCell = snapLiveCell(liveHeldCell, rawLiveCell);
    return { cell: liveHeldCell, radiusKm: liveRingRef.radiusKm };
  };

  /** Ordered zone list from home + saved places + live position. */
  const currentZones = (): TalkZone[] => talkZones(
    home ? { cell: home.cell, radiusKm: home.radiusKm, placeName: home.placeName } : undefined,
    session.places,
    liveZoneRef(),
  );

  /** Zone id → TalkZone lookup for the per-event decode path. Refreshed in
   *  reconcileTalk() (the single choke-point for every zone-set change), so the
   *  message handler reads it without rebuilding the list — and re-snapping the
   *  live cell — on each inbound message. */
  let talkZoneIndex = new Map<string, TalkZone>();

  /** Resolve talkZoneId in currentZones; fall back to the first zone (or home-cell default). */
  const selectedZone = (): TalkZone => {
    const zones = currentZones();
    return zones.find((z) => z.id === talkZoneId)
      ?? zones[0]
      ?? { id: "home", name: "", cell: youHomeCell(), radiusKm: home?.radiusKm ?? 15, kind: "home" };
  };

  const zonePicker = new ZonePicker({
    t: tt,
    onZone: (id) => {
      talkZoneId = id;
      feeds.markSeen(id);
      reconcileTalk();
      render();
    },
  });

  const msgSubs = new MessageSubscriptions(session.client, (zoneId, evt) => {
    void (async () => {
      const z = talkZoneIndex.get(zoneId);
      if (!z || !z.cell) return;
      const ctx = {
        areaSet: new Set(areaCells(z.cell, z.radiusKm)),
        regionSet: new Set(regionCells(z.cell, kmToRegionK(talkRegionKm))),
        homeCell: z.cell,
      };
      const entry = await decodeMessage(evt, ctx, (sealed) => session.client.openBroadcast(sealed));
      if (!entry) return;
      const added = feeds.ingest(zoneId, entry, talkZoneId);
      // (talkGate flips when the selected zone's history backfill COMPLETES — see onBackfilled —
      // not on the first message, so "no messages yet" is only shown once history is known.)
      if (!added || activeTabFromHash() !== "talk") return;
      if (zoneId === talkZoneId) {
        talkFeed.ingest(feeds.store(talkZoneId).listScope(talkScope), session.did, clockSource.nowMs());
      } else {
        zonePicker.setUnread((id) => feeds.unread(id), talkZoneId); // badge-only, no feed rebuild
      }
    })();
  }, {
    onBackfilled: (zoneId) => {
      if (zoneId !== talkZoneId) return;
      talkGate.markLoaded();
      if (activeTabFromHash() === "talk") talkFeed.render(feeds.store(talkZoneId).listScope(talkScope), session.did, clockSource.nowMs(), talkRenderMode, talkGate.state);
    },
    // One call per history window scanned: keeps the gate in "checking" (re-arms its silence
    // timer) and shows "backlog n/100" instead of a false "still trying".
    onBackfillProgress: (zoneId, p) => {
      if (zoneId !== talkZoneId) return;
      talkGate.progress("windows", p.done, p.total);
      if (activeTabFromHash() === "talk" && !talkGate.loaded) talkFeed.render(feeds.store(talkZoneId).listScope(talkScope), session.did, clockSource.nowMs(), "preserve", talkGate.state, talkGate.progress_);
    },
  });

  /** Reconcile zone subscriptions: open/close subs to match the current zone set and
   *  scope. A no-op when not on the Talk tab. Replaces the old subscribeMessages(). */
  const reconcileTalk = () => {
    if (activeTabFromHash() !== "talk") return;
    const zones = currentZones().filter((z) => z.cell);
    talkZoneIndex = new Map(zones.map((z) => [z.id, z]));
    feeds.reconcile(zones.map((z) => z.id));
    msgSubs.reconcile(buildZoneSpecs(zones, talkZoneId, talkScope, talkRegionKm));
  };

  const talkBody = () => {
    const sel = selectedZone();
    zonePicker.render(currentZones(), sel.id, (id) => feeds.unread(id));
    talkFeed.render(feeds.store(sel.id).listScope(talkScope), session.did, clockSource.nowMs(), talkRenderMode, talkGate.state, talkGate.progress_);
    return talkTab({
      t: tt,
      scope: talkScope,
      regionKm: talkRegionKm,
      zonePickerEl: zonePicker.element,
      feedEl: talkFeed.element,
      canPost: !!sel.cell,
      onScope: (s) => { talkScope = s; reconcileTalk(); render(); },
      onRegionKm: (km) => { talkRegionKm = km; reconcileTalk(); render(); },
      draft: talkDraft,
      onDraft: (txt) => { talkDraft = txt; },
      onSend: (text) => publishMessage(session.client, { text, lang: session.locale, cell: sel.cell }),
    });
  };

  let prevTab: TabId | null = null;
  // Set once start() runs (after onboarding completes / for an already-onboarded user).
  // Background renders (bgRender) are suppressed until then so they can't replace the
  // onboarding flow with the tabbed shell before the hashchange listener is attached.
  let started = false;
  const render = () => {
    syncLiveFollow();
    // /get route (QR/desktop deep-link): normalize the hash, then open the install
    // modal after this render mounts the shell (browser-only). Native/installed ignore it.
    if (location.hash.replace(/^#\/?/, "") === "get") {
      history.replaceState(null, "", "#/");
      if (appCtx === "browser") queueMicrotask(openInstall);
    }
    const tab = activeTabFromHash();
    // Subscription gating: stop streaming when leaving Talk (retain seq), resume on
    // return (replays the gap). Returning anchors at the last-seen message.
    if (prevTab === "talk" && tab !== "talk") void msgSubs.pause();
    if (tab === "talk" && prevTab !== "talk") {
      feeds.markSeen(talkZoneId);
      reconcileTalk();
      talkRenderMode = "preserve";
    }
    // The view: scope only exists while browsing the Now map; close it on leave so we
    // stop streaming the viewport area the user is no longer looking at.
    if (prevTab === "now" && tab !== "now") {
      areas.closeScope("view");
      if (rawShown) { rawLayer.deactivate(); rawShown = false; }
    }
    const body =
      tab === "help" ? helpBody() :
      tab === "you" ? youBody() :
      tab === "beacon" ? beaconBody() :
      tab === "talk" ? talkBody() :
      tab === "commons" ? transparencyScreen({
        t: tt, state: commonsState, nowMicros: Date.now() * 1000, // wallclock-allow: display projection (gate-30 excludes apps/webapp)
        hostCurrency: hostCurrencyOf(),
        accountsBasis: [...commonsState.costs].sort((a, b) => Date.parse(b.period.start) - Date.parse(a.period.start))[0]?.accountsBasis ?? "subscribers",
        onChipIn: openChipIn,
      }) :
      nowBody();
    mountTab(tab, body);
    if (tab === "talk") {
      talkFeed.afterMount();
      talkRenderMode = "reset"; // subsequent same-tab renders (scope/region) reset to follow
    }
    // Engagement-gated install hint (M3 Phase 3): only for an engaged mobile-browser
    // visitor who hasn't dismissed it; sits below the alert layers.
    if (shouldShowInstallHint({
      context: appCtx, target: appTarget,
      engaged: !!session.settings.installEngaged,
      dismissed: !!session.settings.installHintDismissed,
    })) {
      hintHost.replaceChildren(installHint({
        t: tt,
        onOpen: openInstall,
        onDismiss: () => void session.saveSettings({ installHintDismissed: true }).then(render),
      }));
    } else {
      hintHost.replaceChildren();
    }
    prevTab = tab;
  };

  // Background re-render driven by streaming events (incident snapshots, live-position
  // updates, matcher pushes) — these arrive ~1×/s. A full render() rebuilds the active
  // tab's BODY, which would destroy an in-body modal sheet the user is mid-way through
  // (the report sheet / ask-for-help sheet live in the tab body, not overlayHost) and
  // needlessly churn the map under an open detail overlay. So suppress the background
  // render while any overlay (overlayDismiss set) or in-body sheet is open; the overlay
  // teardown renders once on close, so the body is fresh again when the user returns.
  const sheetOpen = (): boolean => !!rootEl.querySelector("[data-sheet=report],[data-sheet=create-request]");
  const bgRender = (tab: TabId): void => {
    // `started` gating is what keeps the E9 timer (and any stream-driven render) from
    // ejecting the user from onboarding before start() wires the hashchange listener —
    // see canBackgroundRender for the full rationale.
    if (!canBackgroundRender({
      started,
      activeTab: activeTabFromHash(),
      targetTab: tab,
      overlayOpen: overlayDismiss !== null,
      sheetOpen: sheetOpen(),
    })) return;
    render();
  };
  void pollPulse(); // first relay pulse now, then every 30 s (calls bgRender, defined above)
  setInterval(() => { void pollPulse(); }, 30_000); // wallclock-allow: UI cadence (apps/webapp excluded from gate-30)

  // E9: re-render the Now tab on a fixed cadence so stale incidents fade and
  // worsening ones re-sort even when no new events arrive (markers/rows otherwise
  // only update on incident events). nowMs is read fresh from the sim-aware clock.
  setInterval(() => { bgRender("now"); }, 30_000); // wallclock-allow: UI cadence (apps/webapp excluded from gate-30)

  // Active monitored rings: home (if set) + saved places. The live ring is
  // appended by the watchPosition lifecycle (Task 9) via liveRingRef.
  let liveRingRef: MonitoredRing | null = null;
  // Exact GPS fix for display (not coarsened to cell center); null when live-follow is off.
  let liveExactRef: { lat: number; lng: number } | null = null;
  const monitoredRings = (): MonitoredRing[] => buildMonitoredRings(home, session.places, liveRingRef);
  const primaryRingsArr = (): MonitoredRing[] => monitoredRings().filter((r) => r.kind !== "saved");
  const secondaryRingsArr = (): MonitoredRing[] => monitoredRings().filter((r) => r.kind === "saved");

  // Server-push registration: only when the user has enabled notifications AND we can get
  // push creds (VAPID key present + permission granted). Re-runs on home/place/settings
  // changes; the dispatcher keys by identity so each POST overwrites the prior cells.
  let pushCreds: { endpoint: string; p256dh: string; auth: string } | null = null;
  const publishSubscription = async (): Promise<void> => {
    if (!session.settings.notificationsEnabled) return;
    try {
      if (!pushCreds && "serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await subscribeForPush(reg, vapidKeyFromMeta());
        const j = sub?.toJSON?.();
        if (j?.endpoint && j.keys?.p256dh && j.keys?.auth) {
          pushCreds = { endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth };
        }
      }
      await publishAlertSubscription({
        client: session.client, rings: monitoredRings(), settings: session.settings,
        locale: session.locale, push: pushCreds, postUrl: `${dispatcherBaseUrl()}/subscriptions`,
      });
    } catch (err) {
      console.warn("[alerts] subscription publish failed (in-app alerts unaffected)", err);
    }
  };

  // Opt-in to notifications. Request OS permission FIRST, synchronously inside the
  // toggle's user gesture — deferring it behind awaits makes the browser drop the
  // prompt silently (the bug: users were only ever asked for location, never for
  // notifications, so no push subscription was ever created). Then persist + subscribe
  // and toast the outcome so the user always gets feedback instead of silence.
  const enableNotifications = async (): Promise<void> => {
    const perm = await requestNotificationPermission();
    await session.saveSettings({ notificationsEnabled: true });
    if (perm === "granted") await publishSubscription();
    // notifOutcome splits granted-but-no-push into nopush (no VAPID key on this page)
    // vs subfail (key present, device subscribe failed). For subfail, distinguish a
    // device with NO push service at all (de-Googled / no FCM, Firefox autopush) — that
    // can't be retried away, so say so specifically instead of a generic failure.
    const outcome = notifOutcome(perm, !!vapidKeyFromMeta(), !!pushCreds);
    const msgKey = outcome === "subfail" && isNoPushServiceError(lastSubscribeError())
      ? "you.notif.subfail.noservice"
      : `you.notif.${outcome}`;
    toast(tt(msgKey));
    // Anything but a clean "enabled" → show the raw diagnostic (incl. the real
    // subscribe() error) as a persistent tap-to-dismiss toast, since on an installed
    // PWA there is no console to read.
    if (outcome !== "enabled") {
      toastLog("push diagnostics", pushDiagText({
        permission: perm, hasKey: !!vapidKeyFromMeta(), hasCreds: !!pushCreds, subscribeError: lastSubscribeError(),
      }));
    }
    render();
  };

  const evaluate = () => {
    const all = incidents.list();
    const primary = placesStatus(all, primaryRingsArr(), 0);
    // Pass `all` too: the engine adds downwind incidents whose cone reaches the home zone
    // even when the fire is beyond the radius.
    if (engine) controller.route(coalescer.coalesce(engine.evaluate(primary.inRadius, all, spread)));
    const primarySubjects = new Set(primary.inRadius.map((e) => e.view.subject));
    const watched = watchedIncidents(all, secondaryRingsArr(), primarySubjects);
    // Watched-zone (saved-place) alerts are intentionally silent: alert sound is scoped to the
    // home/primary zone (spec 2026-06-28). These surface visually only — no chime() here.
    for (const a of watchedNotifier.notify(watched)) {
      const el = pushToast({ title: tt("now.watched.alert", { place: a.zoneName ?? tt("you.loc.placeUnnamed") }), body: "" });
      el.setAttribute("role", "status");
      el.setAttribute("data-toast", a.view.subject);
      alertToasts.add(el, { autoDismissMs: 9000, onClick: () => openIncident(a.view.subject) });
    }
  };

  // Per-area firehose subscriptions (incident/spread/trust.label/help), deduped by key.
  // Covers BOTH the home watch radius (keyed by cell+radiusKm) and the browsed map area
  // (keyed by the res-6 cell of the viewport center), over the whole cell set covering
  // each area. Both feeds drive the same IncidentStore/SpreadStore — but only monitored-ring
  // incidents alert (evaluate() feeds the engine placesStatus(monitoredRings()).inRadius), so
  // browsing surfaces fires elsewhere as markers without alerting.
  const ZONE_LOAD_TIMEOUT_MS = 6000;
  const incidentsGate = new ZoneLoadGate(ZONE_LOAD_TIMEOUT_MS, () => bgRender("now"));
  const talkGate = new ZoneLoadGate(ZONE_LOAD_TIMEOUT_MS, () => bgRender("talk"));
  const helpGate = new ZoneLoadGate(ZONE_LOAD_TIMEOUT_MS, () => bgRender("help"));
  const areas = new AreaSubscriptions(session.client, {
    // Incident snapshots drive the alert engine + Now tab.
    onIncident: (evt) => {
      noteAreaEvent();
      incidentsGate.markLoaded();
      // Gate on a real display change: re-broadcast snapshots that don't move anything
      // shouldn't rebuild the Now tab every second (time-driven aging is the 30s tick).
      if (!incidents.ingest({ seq: evt.seq, json: evt.json })) return;
      evaluate();
      bgRender("now");
    },
    // Spread estimates (A5) feed in-path severity + cone data. Redraw the cones on the live
    // map (cheap, no full re-render); they carry the incident subject so taps open the detail.
    onSpread: (evt) => {
      noteAreaEvent();
      spread.ingest({ seq: evt.seq, json: evt.json });
      evaluate();
      if (activeTabFromHash() === "now") refreshSpreadCones(nowMap.acquire(home?.cell).handle);
    },
    onWeather: (evt) => {
      noteAreaEvent();
      weather.ingest({ seq: evt.seq, json: evt.json });
    },
    // FH-5: trust.label folding moved to the permanent trust subscription (subscribeTrust).
    // Help firehose in the neighborhood (res-7 parent) — browse + my-activity (matcher A7 = push).
    onHelp: (evt) => {
      noteAreaEvent();
      const subject = (evt.json.subject as string | undefined) ?? (evt.json.id as string | undefined);
      // Requester-side hint: a NEW offer just landed on one of MY requests. Computed
      // before ingest (so "new" means not-yet-folded) and gated on the help tab, like
      // the nearby-match toast, so the startup backfill replay doesn't spam.
      const newOfferOnMine = evt.json.$type === "app.aid.helpRequest.offer" && subject
        ? (() => { const r = help.requestById(subject); return !!r && r.creator === session.did && !r.offers.some((o) => o.offerId === evt.json.id); })()
        : false;
      const pendingBefore = help.pendingSnapshot().length;
      help.ingest({ seq: evt.seq, json: evt.json });
      if (help.pendingSnapshot().length !== pendingBefore) persistHelpPending(); // an echo cleared a pending mark
      // (helpGate flips on the relay's caughtUp — see onHelpCaughtUp — not on the first event,
      // so an empty list is only ever shown once the bounded replay is known to be complete.)
      if (newOfferOnMine && activeTabFromHash() === "help") toast(tt("help.offer.toast"));
      // Refresh the open request overlay live — but NOT while a Done/Can't-make confirm
      // dialog is mounted over it, or a re-mount would silently dismiss the dialog.
      const confirmUp = overlayHost.querySelector('[data-sheet^="help-confirm-"]') !== null;
      if (openRequestId && subject === openRequestId && !confirmUp) void openRequestScreen(openRequestId);
      refreshHelpTabDot();
      bgRender("help");
    },
  }, {
    seedIncidents: async (cells) => seedEventsFromReadModel(await incidentsClient.incidentsInCells(cells)),
    seedSpread: async (cells) => seedSpreadEventsFromReadModel(await spreadClient.spreadInCells(cells)),
    seedWeather: async (cells) => seedWeatherEventsFromReadModel(await weatherClient.weatherInCells(cells)),
    onIncidentSeeded: () => incidentsGate.markLoaded(),
    onIncidentSeedFailed: () => incidentsGate.markStalled(),
    onIncidentSeedAttempt: (n, max) => incidentsGate.progress("attempts", n, max),
    onHelpCaughtUp: () => helpGate.markLoaded(),
  });

  // Periodic incident refresh: re-run the read-model seed for every active zone so a
  // region-wide warning that begins mid-session — delivered via server-side footprint
  // containment, not the (small-radius-missing) point anchors — is pulled in. Immediate on
  // app open / zone change; this catches the in-between. wallclock-allow: UI cadence
  // (apps/webapp excluded from gate-30), like the 30s bgRender tick.
  const AREA_REFRESH_MS = 120_000;
  setInterval(() => { areas.reseedIncidents(); }, AREA_REFRESH_MS);

  // Managed live subscription to identity genesis/rotation events. No geo filter →
  // GLOBAL (matchesFilter skips the cells check when `cells` is undefined), so the
  // IdentityDirectory learns OTHER participants' signing + encryption keys. Without it,
  // need-to-know sealing (publishAccept's location grant to the chosen helper) can't
  // resolve the helper's encryption key and throws "no encryption key resolved". The
  // Subscription auto-folds every identity event into client.directory (client-core
  // subscription.ts), so we only drain it. Backfilled from seq 0 so genesis events that
  // predate this session are folded. Idempotent; opened once at start().
  let identityStreamStarted = false;
  const subscribeIdentities = (): void => {
    if (identityStreamStarted) return;
    identityStreamStarted = true;
    // FH-5: identity (declareKeys/rotateKey) moved to the DURABLE retention class (FH-3),
    // so we read it via class:"durable" — the durable stream is compacted per-LINK
    // (identity.<did>.<linkIdx>), so sinceSeq:0 replays every chain link, then live.
    // Without this the directory never learns keys and need-to-know sealing throws
    // "no encryption key resolved".
    const sub = session.client.subscribe({ types: [...IDENTITY_TYPES], class: "durable", sinceSeq: 0 });
    void (async () => {
      try { for await (const evt of sub) void evt; } // directory auto-ingests; nothing to render
      catch { identityStreamStarted = false; } // stream ended/errored → allow a later reopen
    })();
  };

  // FH-5: trust.label + trust.credential live on the PERMANENT trust stream (full history,
  // no floor/reset). Split out of the per-area ephemeral subscription. The moderation fold
  // (labels.observe → You-tab transparency, §7) consumes label here; credential is carried
  // for the future durable trust-tier resolution (commonsIssuers stays the v1 allowlist).
  let trustStreamStarted = false;
  const subscribeTrust = (): void => {
    if (trustStreamStarted) return;
    trustStreamStarted = true;
    const sub = session.client.subscribe({
      types: ["app.aid.trust.label", "app.aid.trust.credential"],
      class: "trust",
      sinceSeq: 0,
    });
    void (async () => {
      try {
        for await (const evt of sub) {
          if (evt.json.$type === "app.aid.trust.label") {
            labels.observe(evt.json as Parameters<typeof labels.observe>[0]);
            if (activeTabFromHash() === "you") render();
          }
        }
      } catch { trustStreamStarted = false; } // stream ended/errored → allow a later reopen
    })();
  };

  // Default coverage (km) for the browse/viewport subscription when there is no home
  // radius to size it from — generous so panning surfaces incidents across the view.
  const VIEWPORT_KM = 25;

  // Start the home-geofenced firehose subscriptions over the cells covering the WATCH
  // RADIUS (not just the single hex under home), so an incident anywhere in the radius
  // is delivered — matching the tinted radius the user sees. Idempotent + lazy: a no-op
  // until a home exists; keyed by cell+radius so it re-subscribes if either changes.
  const subscribeHome = () => {
    if (!home?.cell) return;
    const [lat, lng] = cellToLatLng(home.cell);
    areas.ensure(`home:${home.cell}:${home.radiusKm}`, cellsWithinRadius(lat, lng, home.radiusKm));
  };

  // Ensure an area subscription per saved place so their incidents reach the in-app union.
  // Idempotent (areas.ensure dedupes by key). Called on start, add-place, and refreshHome.
  const subscribePlaces = () => {
    for (const p of session.places) {
      const [lat, lng] = cellToLatLng(p.cell);
      areas.ensure(`place:${p.cell}:${p.radiusKm}`, cellsWithinRadius(lat, lng, p.radiusKm));
    }
  };

  // Subscribe to the firehose for the currently-viewed map area — the cells covering
  // VIEWPORT_KM around the viewed center. Browsing is for exploring, so coverage is a
  // generous fixed radius (independent of the home watch radius). This surfaces fires
  // OUTSIDE the monitored rings as map markers; they never alert, because the alert engine
  // only ever evaluates incidents within the monitored rings (see evaluate()/placesStatus).
  // Works for browse-only users (no home). Keyed by the res-6 cell of the center so
  // panning to a new coarse area re-subscribes (and dedupes while panning within one).
  const subscribeViewport = (lat: number, lng: number) => {
    if (activeTabFromHash() !== "now") return; // pending pan timer fired after navigation
    // Exclusive "view" scope: entering a new res-6 cell evicts the cell we left, so the
    // viewport streams stay bounded as the map pans/follows (no per-cell subscription leak).
    const cell = latLngToCell(lat, lng, 6);
    areas.ensureExclusive("view", `view:${cell}`, cellsWithinRadius(lat, lng, VIEWPORT_KM));
  };

  // Debounce map moves so panning across many cells opens at most one subscription
  // set per settle (the map already emits 'moveend' only after motion stops, but the
  // initial-center call + rapid re-renders make a guard worthwhile).
  let moveTimer: ReturnType<typeof setTimeout> | null = null;
  let lastCenter = { lat: 0, lng: 0 };
  const currentViewCells = () => cellsWithinRadius(lastCenter.lat, lastCenter.lng, VIEWPORT_KM);
  const onMapCenter = (lat: number, lng: number) => {
    lastCenter = { lat, lng };
    if (moveTimer) clearTimeout(moveTimer);
    // wallclock-allow: browser UI debounce, not a clock read (gate-30 excludes apps/webapp).
    moveTimer = setTimeout(() => {
      subscribeViewport(lat, lng);
      if (rawShown) void rawLayer.activate(cellsWithinRadius(lat, lng, VIEWPORT_KM));
    }, 600);
  };

  // One PaperMap for the Now tab, reused across re-renders (NowMapController owns the
  // viewport seeding via onMapCenter). Rebuilt only when the home cell changes.
  // Camera mode drives the single Now-tab toggle FAB: follow (centering on live) →
  // fit (framed all zones) → free (manually panned). Defaults to follow (live-follow default).
  let cameraMode: "follow" | "fit" | "free" = "follow";
  let liveFollowUnavailable = false;
  const nowMap = new NowMapController({
    onMoveEnd: onMapCenter,
    onIncidentClick: openIncident,
    onSpreadClick: openIncident, // a cone belongs to an incident → open that incident's detail
    onRawReportClick: (id) => {
      const p = rawStore.list().find((r) => r.id === id);
      if (!p) return;
      mountOverlay(reportDetail({ t: tt, report: p, onBack: closeOverlay }));
    },
    // A manual pan only disengages an ACTIVE follow → free. When already in fit (fitAll
    // itself fires this), the guard leaves the mode alone so we don't clobber fit → free.
    onFollowDisengaged: () => { if (cameraMode === "follow") { cameraMode = "free"; bgRender("now"); } },
  });

  // Live-follow: foreground-only geolocation watch → live ring + union re-eval. Position
  // stays on-device (never added to the push subscription; see subscriptionCells).
  const liveFollow = new LiveFollowController({
    watch: () => navigator.geolocation.watchPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        const radiusKm = session.settings.liveFollow?.radiusKm ?? 5;
        liveRingRef = { kind: "live", cell: latLngToCell(lat, lng, 8), radiusKm };
        liveExactRef = { lat, lng };
        evaluate();
        // bgRender (not render): a GPS tick must NOT rebuild the Now-tab body while the
        // report sheet is open in it — that was closing the report screen every ~1s.
        bgRender("now");
      },
      (err: GeolocationPositionError) => {
        // PERMISSION_DENIED (code 1) is terminal: reflect "unavailable" and turn the
        // opt-in off so the user isn't misled into thinking they're being followed.
        // Transient errors (POSITION_UNAVAILABLE=2, TIMEOUT=3) keep the watch armed.
        if (err && err.code === 1) {
          liveFollowUnavailable = true;
          void session.setLiveFollow({ enabled: false, radiusKm: session.settings.liveFollow?.radiusKm ?? 5 })
            .then(() => { syncLiveFollow(); render(); });
        }
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 },
    ),
    clear: (id) => {
      navigator.geolocation.clearWatch(id);
      liveRingRef = null;
      liveExactRef = null;
      nowMap.acquire(home?.cell).handle.setLivePosition(null);
    },
  });

  // Keep-awake: hold a screen Wake Lock while actively following live position on the
  // foreground Now tab, unless the user opted out (settings.keepAwake). A sleeping device
  // can't surface an alert — the web-app limitation the You-tab warning names.
  const wakeLock = new WakeLockController({
    acquire: async () => {
      const wl = (navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<ReleasableLock> } }).wakeLock;
      return wl?.request ? wl.request("screen") : null;
    },
  });

  const syncLiveFollow = () => {
    const visible = typeof document === "undefined" || document.visibilityState === "visible";
    const onNow = activeTabFromHash() === "now";
    const liveEnabled = !!session.settings.liveFollow?.enabled;
    liveFollow.update({ enabled: liveEnabled && "geolocation" in navigator, visible, onNow });
    // Screen stays awake only while actively following (live-follow on + camera locked on)
    // on the visible Now tab; opt-out via settings.keepAwake (default on).
    void wakeLock.update({
      wanted: session.settings.keepAwake !== false && liveEnabled && cameraMode === "follow" && onNow && visible,
    });
  };

  const start = () => {
    started = true; // enable background renders now that the listener (below) is wired
    addEventListener("hashchange", render);
    addEventListener("visibilitychange", syncLiveFollow);
    render();
    // Commons mirror feed (CL-5): subscribe to cost+contribution on the configured
    // commonsCell (full backfill) and re-render the surfaces that show the mirror.
    const cCell = commonsCell();
    if (cCell) {
      const feed = new CommonsFeed(session.client, {
        cell: cCell,
        issuers: commonsIssuers(),
        onChange: (s) => {
          // My deposit landed on the firehose since I opened chip-in → thank-you toast,
          // once. Surfaces even if the modal was already closed (the watcher's ~10s lag).
          if (chipInBaseline !== null && ownContribs(s) > chipInBaseline) {
            chipInBaseline = null;
            toast(tt("commons.received"));
            chipInOnReceipt?.(); // live in-modal spinner → thank-you, if open
          }
          commonsState = s;
          const tab = activeTabFromHash();
          if (tab === "you" || tab === "beacon" || tab === "commons" || tab === "now") render();
        },
      });
      feed.start();
    }
    subscribeHome();
    subscribePlaces();
    subscribeIdentities(); // learn others' keys so need-to-know sealing (accept) resolves
    subscribeTrust(); // FH-5: moderation labels + credentials off the permanent trust stream
    // Voice rotation: multi-variant tonal lines (the dark voice especially) advance to
    // the next variant every other hour. Check on a coarse interval and re-render only
    // when the rotation bucket actually ticks over.
    let toneBucket = Math.floor(Date.now() / TONE_ROTATE_MS); // wallclock-allow: webapp excluded from gate-30
    setInterval(() => {
      const b = Math.floor(Date.now() / TONE_ROTATE_MS); // wallclock-allow
      if (b === toneBucket) return;
      toneBucket = b;
      void session.refreshTone(Date.now()).then((changed) => { if (changed) render(); }); // wallclock-allow
    }, 5 * 60 * 1000);
  };

  // Adopt a home set after startup (You-tab picker / radius change): rebind reactive
  // state from the persisted session, (lazily) start subscriptions, and re-render — no reload.
  const refreshHome = () => {
    setHomeState(session.home);
    subscribeHome();
    subscribePlaces();
    void publishSubscription();
    reconcileTalk();
    render();
  };

  if (!session.isOnboarded) {
    rootEl.replaceChildren(
      onboardingFlow({ session, t: tt, defaultCell: defaultCell(), geolocate: geolocateCell, done: () => { setHomeState(session.home); start(); } }),
    );
  } else {
    start();
  }
}
